// La BIBLIOTECA de adjuntos del Agente: qué pestañas tiene, cómo se convierte cada cosa
// del catálogo en un adjunto del motor y cómo se sabe si ya está enganchado —
// ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente.
//
// Es un módulo PURO (sin React ni red) para que los ids sean estables y testeados: el
// panel alterna (tocar lo que ya está adjunto lo saca) y eso sólo funciona si el mismo
// producto / pose / fondo / imagen produce siempre el mismo id. Los selectores modales
// (`SelectoresDeAdjuntos`) usan estas mismas funciones, así lo que se engancha desde un
// lado se reconoce desde el otro, y adjuntar dos veces lo mismo no lo duplica (el motor
// reemplaza por id).

import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { esNombreDeVideo } from '../../utils/posterDeVideo';
import { rubroVeMoodboards } from '../../lib/rubro';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';
import type { AccionMenuMas } from './menuMasItems';

export type PestanaBiblioteca =
  | 'productos'
  | 'modelos'
  | 'poses'
  | 'fondos'
  | 'creaciones'
  | 'subidas'
  | 'moodboards';

/** Las pestañas, en el orden en que se ven. `vacio` es lo que dice la grilla sin nada. */
export const PESTANAS_BIBLIOTECA: { id: PestanaBiblioteca; etiqueta: string; vacio: string }[] = [
  { id: 'productos', etiqueta: 'Productos', vacio: 'Todavía no cargaste productos.' },
  {
    id: 'modelos',
  // "Modelo" en Moda, "Avatar" en el resto: `get` porque la tabla se arma una vez al
  // cargar el módulo y la marca se cambia sin recargar
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
    get etiqueta() {
      return palabraDeModeloActual().tituloPlural;
    },
    get vacio() {
      return `No hay ${palabraDeModeloActual().plural} guardados en tu marca.`;
    },
  },
  { id: 'poses', etiqueta: 'Poses', vacio: 'No hay poses para elegir.' },
  { id: 'fondos', etiqueta: 'Fondos', vacio: 'No hay fondos para elegir.' },
  { id: 'creaciones', etiqueta: 'Creaciones', vacio: 'Todavía no generaste imágenes.' },
  { id: 'subidas', etiqueta: 'Subidas', vacio: 'Todavía no subiste imágenes al chat.' },
  // Sólo Moda: la ve `pestanasVisibles`
  // (ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente).
  { id: 'moodboards', etiqueta: 'Estilos', vacio: 'Todavía no armaste ningún estilo.' },
];

/**
 * Las pestañas que ve ESTA marca. Moodboards existe sólo para Moda e Indumentaria, con
 * el mismo gate que el tab de Mi marca (`rubroVeMoodboards`): quién ve qué lo decide el
 * rubro de la marca activa, y la biblioteca no puede ofrecer una carpeta que la pantalla
 * de la marca no muestra.
 */
export const pestanasVisibles = (rubro: string | null | undefined) =>
  PESTANAS_BIBLIOTECA.filter((p) => p.id !== 'moodboards' || rubroVeMoodboards(rubro));

/** Las ALTAS: qué se puede agregar desde cada pestaña, además de elegir. Cada una es
 *  una tarjeta al principio de la grilla (la misma "tarjeta de acción" que encabeza
 *  las grillas de los selectores) que abre la pantalla de alta correspondiente —la
 *  MISMA que usan los selectores de los modos— (ADR-2026-09-12-la-biblioteca-agrega-en-
 *  todas-las-pestanas). Creaciones no tiene alta: se generan desde el chat. */
export type AltaDeBiblioteca =
  | 'subir-producto'
  | 'crear-modelo'
  | 'subir-modelo'
  | 'subir-pose'
  | 'subir-fondo'
  | 'subir-imagen';

export const ALTAS_POR_PESTANA: Record<
  PestanaBiblioteca,
  {
    id: AltaDeBiblioteca;
    etiqueta: string;
    detalle: string;
    titulo: string;
    /**
     * El renglón que va DEBAJO del título cuando el alta ocupa una ventana propia y no
     * una tarjeta: qué se está por hacer, en una línea
     * (ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas). En la tarjeta
     * de la grilla sigue yendo `detalle`, que es de dos o tres palabras.
     */
    bajada: string;
  }[]
> = {
  productos: [
    {
      id: 'subir-producto',
      etiqueta: 'Subir producto',
      detalle: 'Fotos y nombre',
      titulo: 'Subir nuevo producto',
      bajada: 'Sumá las fotos del producto. El nombre es opcional: si lo dejás vacío lo completa Atlas.',
    },
  ],
  modelos: [
    {
      id: 'crear-modelo',
      get etiqueta() {
        return `Crear ${palabraDeModeloActual().singular}`;
      },
      detalle: 'Describilo',
      get titulo() {
        return `Crear ${palabraDeModeloActual().singular}`;
      },
      get bajada() {
        return `Definí cómo se ve el ${palabraDeModeloActual().singular} para que se ajuste al estilo de tu marca.`;
      },
    },
    {
      id: 'subir-modelo',
      etiqueta: 'Subir foto',
      detalle: 'Usá una persona real',
      get titulo() {
        return `Subir tu ${palabraDeModeloActual().singular}`;
      },
      get bajada() {
        return `Una foto donde se vea bien el rostro. Queda guardada como ${palabraDeModeloActual().singular} tuyo para reusar.`;
      },
    },
  ],
  poses: [
    {
      id: 'subir-pose',
      etiqueta: 'Subir pose',
      detalle: 'Usá una foto tuya',
      titulo: 'Subí tu propia pose',
      bajada: 'De la foto se copia sólo la postura. Queda guardada en "Mis poses" para usarla cuando quieras.',
    },
  ],
  fondos: [
    {
      id: 'subir-fondo',
      etiqueta: 'Subir fondo',
      detalle: 'Tu propio escenario',
      titulo: 'Subí tu fondo',
      bajada: 'Una imagen del lugar o escenario que querés usar. Queda guardada en tu biblioteca para reusarla.',
    },
  ],
  creaciones: [],
  subidas: [
    {
      id: 'subir-imagen',
      etiqueta: 'Subir imagen',
      detalle: 'Desde tu dispositivo',
      titulo: 'Subir imagen',
      bajada: 'Se adjunta al mensaje que estás escribiendo.',
    },
  ],
  // Moodboards agrega ADENTRO de su propia pantalla (la card "Nueva carpeta" de la grilla
  // de carpetas y "Agregar fotos" dentro de cada una): no hay tarjeta de alta acá.
  moodboards: [],
};

/** El ancho de la columna, en píxeles, a partir del ancho del contenedor: una fracción
 *  con piso y techo, como la columna de gráficos. Va en píxeles y no en CSS porque la
 *  apertura y el cierre ANIMAN el ancho desde cero, y un `clamp()` en porcentaje no se
 *  puede interpolar contra el contenido que tiene que quedar a su medida final
 *  (ADR-2026-09-12-la-biblioteca-se-abre-y-se-cierra-empujando). */
export const ANCHO_MINIMO_BIBLIOTECA = 440;
export const ANCHO_MAXIMO_BIBLIOTECA = 640;
export const FRACCION_BIBLIOTECA = 0.46;
export const anchoDeBiblioteca = (anchoContenedor: number): number =>
  Math.min(
    ANCHO_MAXIMO_BIBLIOTECA,
    Math.max(ANCHO_MINIMO_BIBLIOTECA, Math.round(anchoContenedor * FRACCION_BIBLIOTECA)),
  );

/** A qué pestaña lleva cada card del menú "+". `archivos` abre el explorador de
 *  archivos, no la biblioteca. */
export const pestanaDeAccion = (accion: AccionMenuMas): PestanaBiblioteca | null => {
  switch (accion) {
    case 'producto':
      return 'productos';
    case 'modelo':
      return 'modelos';
    case 'pose':
      return 'poses';
    case 'fondo':
      return 'fondos';
    case 'generada':
      return 'creaciones';
    case 'archivos':
      return null;
  }
};

export const adjuntoDeProducto = (p: {
  id: string | number;
  name: string;
  imageUrl: string;
}): ChatAttachment => ({
  kind: 'product',
  id: `prod-${p.id}`,
  productId: String(p.id),
  name: p.name,
  imageUrl: p.imageUrl,
});

/** Un chip por modelo (`model-<id>`): se pueden adjuntar varios y el agente los reparte
 *  como pida el usuario (ADR-2026-09-13-varios-modelos-en-la-caja). Es la misma selección
 *  `preset` que produce el selector de modelos. */
export const adjuntoDeModelo = (m: {
  id: string | number;
  nombre: string;
  thumbnailUrl: string;
}): ChatAttachment => ({
  kind: 'model',
  // Un id POR modelo: con `model-attachment` fijo, dos modelos compartían el chip y el
  // segundo pisaba al primero (ADR-2026-09-13-varios-modelos-en-la-caja).
  id: `model-${m.id}`,
  selection: { mode: 'preset', id: String(m.id), name: m.nombre, imageUrl: m.thumbnailUrl },
  name: m.nombre,
  imageUrl: m.thumbnailUrl,
});

export const adjuntoDePose = (p: { id: string | number; nombre: string; imageUrl: string }): ChatAttachment => ({
  kind: 'referencia',
  id: `pose-${p.id}`,
  tipo: 'pose',
  name: p.nombre,
  imageUrl: p.imageUrl,
});

export const adjuntoDeFondoDeBiblioteca = (f: {
  id: string | number;
  nombre: string;
  imageUrl: string;
}): ChatAttachment => ({
  kind: 'referencia',
  id: `fondo-biblioteca-${f.id}`,
  tipo: 'fondo',
  name: f.nombre,
  imageUrl: f.imageUrl,
});

/** Los fondos guardados no tienen id en su selección: los distingue el archivo. */
export const adjuntoDeFondoGuardado = (f: {
  fileName: string;
  nombre: string;
  imageUrl: string;
}): ChatAttachment => ({
  kind: 'referencia',
  id: `fondo-guardado-${f.fileName}`,
  tipo: 'fondo',
  name: f.nombre,
  imageUrl: f.imageUrl,
});

export const adjuntoDeGenerada = (g: {
  id: string | number;
  storagePath: string;
  previewUrl: string;
  bucket: string;
  esVideo?: boolean;
  duracionSeg?: number;
  nombre?: string;
}): ChatAttachment =>
  // Un VIDEO de Creaciones se adjunta como video: el agente lo analiza (`ver_video`) o
  // lo usa de referencia. Como imagen viajaba el mp4 en un bloque de foto y Anthropic
  // rechazaba el mensaje entero con un 400
  // (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video).
  g.esVideo || esNombreDeVideo(g.storagePath)
    ? {
        kind: 'video',
        id: `gen-${g.id}`,
        name: g.nombre || 'Video generado',
        storagePath: g.storagePath,
        bucket: g.bucket,
        bytes: 0,
        duracionSeg: g.duracionSeg ?? 0,
      }
    : {
        kind: 'image',
        id: `gen-${g.id}`,
        storagePath: g.storagePath,
        previewUrl: g.previewUrl,
        bucket: g.bucket,
      };

/** Una imagen que el usuario ya subió al chat (vive en el bucket de assets, el default
 *  de `kind: 'image'`): se vuelve a enganchar sin subirla de nuevo. */
export const adjuntoDeSubida = (s: { storagePath: string; previewUrl: string }): ChatAttachment => ({
  kind: 'image',
  id: `subida-${s.storagePath}`,
  storagePath: s.storagePath,
  previewUrl: s.previewUrl,
});

/**
 * Una foto de un moodboard: una REFERENCIA (como la pose y el fondo), no una imagen del
 * usuario: no ocupa lugar en el tope de archivos del "+" y el modelo la recibe etiquetada
 * con el nombre de la carpeta (ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-
 * agente). `imageUrl` tiene que ser la firma del ORIGINAL (`/object/sign/`), no la
 * miniatura con `transform`: las firmas con transformación no las reconoce
 * `leerFirma`, y sin renovación el modelo la recibiría vencida a la hora.
 */
export const adjuntoDeMoodboard = (f: {
  fotoId: string;
  carpeta: string;
  imageUrl: string;
}): ChatAttachment => ({
  kind: 'referencia',
  id: `moodboard-${f.fotoId}`,
  tipo: 'moodboard',
  name: f.carpeta,
  imageUrl: f.imageUrl,
});

/** El id del chip de una carpeta: el mismo con todas las fotos o con algunas. */
export const idDeCarpetaDeMoodboard = (moodboardId: string): string => `moodboard-carpeta-${moodboardId}`;

/**
 * Un moodboard ENTERO: un puntero a la carpeta, no sus fotos
 * (ADR-2026-09-13-el-agente-ve-los-moodboards). El agente las mira con `ver_moodboard`
 * de a diez, como el lote; así doce fotos no se arrastran en todo el historial.
 *
 * Con `fotoIds`, sólo esas: el selector de fuentes deja elegir la carpeta y desmarcar
 * (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). Si están todas, no se guardan:
 * es la carpeta entera, y así una foto que se sume después también entra. El id es el de
 * la carpeta en los dos casos: un chip por carpeta, y volver a elegirla lo reemplaza.
 */
export const adjuntoDeCarpetaDeMoodboard = (c: {
  id: string;
  nombre: string;
  cantidadFotos: number;
  portada: string[];
  fotoIds?: string[];
}): ChatAttachment => ({
  kind: 'moodboard',
  id: idDeCarpetaDeMoodboard(c.id),
  moodboardId: c.id,
  name: c.nombre,
  cantidadFotos: c.cantidadFotos,
  ...(c.fotoIds && c.fotoIds.length < c.cantidadFotos ? { fotoIds: c.fotoIds } : {}),
  imageUrl: c.portada[0] ?? null,
});

/** ¿Este adjunto ya está enganchado? Por id estable — el modelo también, desde que cada
 *  uno lleva el suyo (ADR-2026-09-13-varios-modelos-en-la-caja). */
export const yaAdjunto = (adjuntos: ChatAttachment[], candidato: ChatAttachment): boolean =>
  adjuntos.some((a) => a.id === candidato.id);

/** Un chip de imagen cuyo archivo es un video → el mismo chip, como video. El resto,
 *  igual. Puro (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video). */
export function adjuntoCorregidoSiEsVideo(a: ChatAttachment): ChatAttachment {
  if (a.kind !== 'image' || !esNombreDeVideo(a.storagePath)) return a;
  return {
    kind: 'video',
    id: a.id,
    name: 'Video generado',
    storagePath: a.storagePath,
    ...(a.bucket ? { bucket: a.bucket } : {}),
    bytes: 0,
    duracionSeg: 0,
  };
}
